import React, { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './SideBarMenu.css';
import { Logo } from '@/Shared/UI/Components';
import {
	useAppNavigation,
	useOutsideClick,
	RoleHelper,
	BREAD_CRUMPS_LABELS,
	FaHome,
	FaTimes,
	FaBars,
	USER_ROLES,
	notificationSound,
} from '@/Shared';

import {
	useActivity,
	useAppSelector,
	useBirthWish,
	useContact,
	useDocument,
	useLease,
	useMandate,
	useMatching,
	useNeed,
	usePermission,
	usePlanning,
	useProperty,
	useTask,
	useTransaction,
	useUser
} from '@/OneLiners';

import { DOMAIN_CONFIGS } from '@/Shared/Config/domain-config';


/**
 * =====================================================
 * SideBarMenu
 * =====================================================
 *
 * Main lateral navigation component.
 *
 * Responsibilities:
 * - Displays the main application navigation
 * - Adapts menu items based on user role (admin / non-admin)
 * - Displays unread badges for domain-driven events
 * - Handles responsive expand / collapse behavior
 * - Synchronizes navigation state with current route
 *
 * Note:
 * -----
 * Some domain hooks are intentionally called here
 * even if their returned data is not used.
 *
 * Reason:
 * - These domains expose unread badges
 * - Calling the hook ensures the poller is active
 * - Without the hook call, no background update → no badge
 *
 * Only badge-enabled domains are concerned.
 *
 * This component remains UI-focused and contains
 * no business or data logic.
 *
 * Author: Samano CASTRE
 * Date: July 16, 2025
 */
const SideBarMenu: FC = () => {

	/**
	 * =====================================================
	 * Domain hooks registration (poller enrollment)
	 * =====================================================
	 *
	 * These hooks are called for their SIDE EFFECTS only.
	 *
	 * - No returned data is consumed
	 * - No rendering dependency exists
	 *
	 * Their sole purpose here is to:
	 * - Register the domain poller
	 * - Enable background synchronization
	 * - Feed the badge system via fetchSince
	 *
	 * Removing a hook call here will silently disable:
	 * - Polling
	 * - Badge updates
	 * for the corresponding domain.
	 */
	useContact();
	useMandate();
	useLease();
	useProperty();
	useNeed();
	useMatching();
	useTransaction();
	useActivity();
	usePlanning();
	useTask();
	useDocument();
	useBirthWish();

	/**
	 * =====================================================
	 * Badge state
	 * =====================================================
	 *
	 * Badges are maintained independently from domain entity states.
	 *
	 * They represent:
	 * - A set of unread entity IDs per domain
	 * - Fed exclusively by poller fetchSince results
	 *
	 * This avoids:
	 * - Heavy subscriptions to large entity slices
	 * - Unnecessary sidebar re-renders
	 *
	 * Badge presence therefore reflects background activity,
	 * not the current content of domain tables.
	 */
	const badges = useAppSelector(state => state.uiState.badges);


	/**
	 * =====================================================
	 * Refs & layout state
	 * =====================================================
	 *
	 * - dropdownRef: sidebar container
	 * - triggerRef: toggle button
	 *
	 * Used by `useOutsideClick` to automatically collapse
	 * the sidebar when clicking outside.
	 */
	const dropdownRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);


	/**
	 * =====================================================
	 * Routing & session context
	 * =====================================================
	 */
	const location = useLocation();
	const userSession = useAppSelector(state => state.sessionState.userSession);

	const isAtLeastAdmin = RoleHelper.isAtLeastAdmin(userSession?.createdByUser?.role ?? "");
	const isSuperAdmin = RoleHelper.isSuperAdmin(userSession?.createdByUser?.role ?? "");

	/**
	 * =====================================================
	 * Navigation state
	 * =====================================================
	 *
	 * - currentNavItem: resolved from route to highlight active item
	 * - isExpanded: sidebar expanded / collapsed state
	 */
	const currentNavItem = Object.keys(BREAD_CRUMPS_LABELS).find(key => location.pathname.includes(key)) ?? "home";
	const [isExpanded, setIsExpanded] = useState(false);
	const { goTo } = useAppNavigation();

	/**
	 * =====================================================
	 * Auto-collapse on outside click
	 * =====================================================
	 */
	useOutsideClick(triggerRef, dropdownRef, () => setIsExpanded(false));

	const menuLinks: ReactNode[] = useMemo(() => {
		return Object.values(DOMAIN_CONFIGS).map(item => {

			const { 
				domainRoute, 
				domainType, 
				domainLabelPl, 
				DomainIcon, 
				requireRole, 
				noDisplayMenu 
			} = item;

			const roleMissMatch = (requireRole === USER_ROLES.ADMIN && !isAtLeastAdmin)
				|| (requireRole === USER_ROLES.SUPER_ADMIN && !isSuperAdmin);

			if (noDisplayMenu || roleMissMatch) {
				return null;
			}

			const badgeSize = badges[item.domainType]?.length;
			const hasBadge = !!badgeSize;

			const readStatusClass = hasBadge ? ' unread-item ' : "";
			const activeStatusClass = domainRoute.includes(currentNavItem) ? ' active' : '';

			return (
					<Link
					    className={`sidebar-item ${activeStatusClass} ${readStatusClass}`} key={domainType}
						to={domainRoute} onClick={() => {
							setIsExpanded(false);
							goTo(domainRoute);
						}}
						aria-label={domainLabelPl}>
						<span className="icon"><DomainIcon/></span>
						{hasBadge && <div className='badge'>{badgeSize}</div>}
						{isExpanded && <span className="label">{domainLabelPl}</span>}
					</Link>
			)
		});
	}, [
		isAtLeastAdmin,
		isSuperAdmin,
		badges,
		currentNavItem,
		isExpanded
	]);

	const previousBadgeCountRef = useRef(0);

	useEffect(() => {
		
		const totalBadges = Object.values(badges)
			.reduce((sum, arr) => sum + (arr?.length ?? 0), 0);

		if (totalBadges > previousBadgeCountRef.current) {
			notificationSound.volume = 0.25;
			notificationSound.play().catch(() => {
				// navigateur a bloqué → on abandonne
			});
		}
		previousBadgeCountRef.current = totalBadges;
		
	}, [badges]);




	/**
	 * =====================================================
	 * UI helpers
	 * =====================================================
	 */
	const toggleSidebar = () => setIsExpanded(!isExpanded);
	const isOnline = !!userSession;

	const getClasses = () => {
		let classes = isExpanded ? 'expanded' : 'collapsed';
		classes += isOnline ? ' online-sidebar' : ' offline-sidebar';
		return classes
	}


	/**
	 * =====================================================
	 * Render
	 * =====================================================
	 */
	return (
		<div className={`SideBarMenu elevation-2 ${getClasses()}`} ref={dropdownRef}>

			<div
				className={!isOnline ? 'Logo-wrapper' : 'sidebar-logo'}>
				<Logo />
			</div>
			{isOnline &&
				<>
					<button
						className="sidebar-toggle"
						aria-label="toggle sidebar button"
						onClick={toggleSidebar}
						ref={triggerRef}>
						{isExpanded ? <FaTimes /> : <FaBars />}
					</button>
					<nav className="sidebar-nav sm-scroll">
							<Link
							    className={`sidebar-item ${'home' === currentNavItem ? ' active' : ''}`}
								aria-label='Accueil'
								to='/home'
								onClick={
									() => {
										setIsExpanded(false);
										goTo('/home');
									}
								}
							>
								<span className="icon"><FaHome /></span>
								{isExpanded && <span className="label">Accueil</span>}
							</Link>
						{menuLinks}
					</nav>

				</>
			}
		</div>
	);
};

export default SideBarMenu;